import { useState, useMemo } from "react";
import { DestinationDetail } from "../../../mocks/destinationDetails";
import { travelTimesMap, TravelSegment, DayTravelTimes } from "../../../mocks/travelTimes";
import { drivingDistancesMap, totalDistancesMap } from "../../../mocks/drivingDistances";
import { elevationDataMap } from "../../../mocks/elevationData";
import ElevationChart from "./ElevationChart";

interface Props {
  destination: DestinationDetail;
}

// ─── Travel segment renderers ────────────────────────────────────────────────

const DayStartBanner = ({ seg }: { seg: TravelSegment }) => {
  if (seg.isWalking) {
    return (
      <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-50 border border-green-100 rounded-xl">
        <div className="w-6 h-6 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
          <i className="ri-walk-line text-green-600 text-xs"></i>
        </div>
        <span className="text-xs text-green-700 font-medium">
          {seg.from && seg.to ? `${seg.from} → ${seg.to}` : "步行前往"}
        </span>
        {seg.walkTime && (
          <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">
            步行 {seg.walkTime}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0">
          <i className="ri-roadster-line text-white text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          {seg.from && seg.to && (
            <p className="text-xs text-gray-500 mb-0.5 truncate">
              {seg.from}
              <i className="ri-arrow-right-line mx-1 text-orange-400"></i>
              {seg.to}
            </p>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            {seg.km && (
              <span className="flex items-center gap-1 text-sm font-bold text-orange-700">
                <i className="ri-route-line text-orange-500 text-xs"></i>
                {seg.km.toLocaleString()} km
              </span>
            )}
            {seg.hours && (
              <span className="flex items-center gap-1 text-sm font-bold text-gray-700">
                <i className="ri-time-line text-gray-400 text-xs"></i>
                自駕 {seg.hours.includes(":") ? seg.hours : `${seg.hours}`} 小時
              </span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full whitespace-nowrap">
            客人可按能力調整
          </span>
        </div>
      </div>
    </div>
  );
};

const BetweenConnector = ({ seg }: { seg: TravelSegment }) => {
  if (seg.isWalking) {
    return (
      <div className="flex items-center gap-1.5 ml-6 my-1">
        <div className="w-px h-3 bg-green-200 ml-1.5"></div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600 border border-green-100">
          <i className="ri-walk-line text-xs"></i>
          <span>步行 {seg.walkTime || ""}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 ml-6 my-1">
      <div className="w-px h-3 bg-orange-200 ml-1.5"></div>
      <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <i className="ri-car-line text-xs"></i>
        {seg.km && <span>{seg.km.toLocaleString()} km</span>}
        {seg.km && seg.hours && <span className="text-amber-400">·</span>}
        {seg.hours && (
          <span>
            {seg.hours.includes(":") ? seg.hours : seg.hours} 小時
          </span>
        )}
        {!seg.km && !seg.hours && <span>車程</span>}
      </span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const DestinationTabs = ({ destination }: Props) => {
  const [activeTab, setActiveTab] = useState<"itinerary" | "attractions" | "cost">("itinerary");

  const tabs = [
    { key: "itinerary" as const, label: "每日行程", icon: "ri-map-2-line" },
    { key: "attractions" as const, label: "景點介紹", icon: "ri-landscape-line" },
    { key: "cost" as const, label: "費用說明", icon: "ri-bill-line" },
  ];

  const totalDist = totalDistancesMap[destination.id] ?? 0;
  const dayDistances = drivingDistancesMap[destination.id] ?? {};

  const cumulativeMap = useMemo(() => {
    const map: Record<number, number> = {};
    let running = 0;
    destination.itinerary.forEach((d) => {
      running += dayDistances[d.day] ?? 0;
      map[d.day] = running;
    });
    return map;
  }, [destination.id, destination.itinerary, dayDistances]);

  const hasDrivingData = totalDist > 0;

  return (
    <section className="w-full px-6 lg:px-12 py-10">
      <div className="max-w-5xl mx-auto">

        {/* Highlights */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4">行程亮點</h2>
          <div className="flex flex-wrap gap-2">
            {destination.highlights.map((h) => (
              <span
                key={h}
                className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 text-sm px-3 py-1.5 rounded-full border border-orange-100"
              >
                <i className="ri-star-fill text-orange-400 text-xs"></i>
                <span>{h}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="mb-10 bg-stone-50 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">路線介紹</h2>
          <p className="text-gray-600 leading-relaxed">{destination.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
            {[
              ...(destination.price !== "需要報價" ? [{ icon: "ri-calendar-line", label: "行程天數", value: destination.duration }] : []),
              { icon: "ri-sun-line", label: "最佳季節", value: destination.bestSeason },
              { icon: "ri-map-pin-line", label: "出發/結束", value: `${destination.startCity} → ${destination.endCity}` },
              { icon: "ri-bar-chart-line", label: "難度", value: destination.difficulty },
            ].map((info) => (
              <div key={info.label} className="bg-white rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center mb-2">
                  <i className={`${info.icon} text-orange-500 text-lg`}></i>
                </div>
                <p className="text-xs text-gray-400 mb-0.5">{info.label}</p>
                <p className="text-sm font-semibold text-gray-900">{info.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Nav */}
        <div className="border-b border-stone-200 mb-8">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Itinerary Tab */}
        {activeTab === "itinerary" && (
          <div>
            {/* Route Distance Summary */}
            {hasDrivingData && (
              <div className="mb-8 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-5 border border-orange-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full">
                      <i className="ri-roadster-line text-white text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">全程自駕里程</p>
                      <p className="text-lg font-bold text-orange-600 leading-none mt-0.5">
                        {totalDist.toLocaleString()} km
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{destination.startCity} → {destination.endCity}</p>
                    {destination.price !== "需要報價" && (
                      <p className="text-sm font-medium text-gray-700 mt-0.5">{destination.duration} · {destination.itinerary.length} 天</p>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-white/70 rounded-full h-3 overflow-hidden border border-orange-100">
                    <div className="bg-gradient-to-r from-orange-400 to-amber-400 h-3 rounded-full w-full"></div>
                  </div>
                  <div className="flex justify-between mt-1.5 text-xs text-gray-400">
                    <span className="flex items-center gap-0.5">
                      <i className="ri-checkbox-blank-circle-fill text-orange-400" style={{ fontSize: "8px" }}></i>
                      出發
                    </span>
                    <span className="flex items-center gap-0.5">
                      終點
                      <i className="ri-map-pin-2-fill text-orange-500" style={{ fontSize: "8px" }}></i>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Elevation Chart */}
            {elevationDataMap[destination.id] && (
              <ElevationChart
                data={elevationDataMap[destination.id]}
                destinationName={destination.name}
              />
            )}

            {/* Day Cards */}
            <div className="space-y-0">
              {destination.itinerary.map((day, idx) => {
                const dayData: DayTravelTimes | undefined = travelTimesMap[destination.id]?.[day.day];
                const dayKm = dayDistances[day.day] ?? 0;
                const cumKm = cumulativeMap[day.day] ?? 0;
                const progress = totalDist > 0 ? Math.round((cumKm / totalDist) * 100) : 0;

                return (
                  <div key={day.day} className="flex gap-5">
                    {/* Timeline */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {day.day}
                      </div>
                      {idx < destination.itinerary.length - 1 && (
                        <div className="w-px bg-orange-100 flex-1 mt-1 min-h-6"></div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-6">
                      {/* Day header */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <h3 className="font-semibold text-gray-900 text-sm leading-snug">{day.title}</h3>
                        {hasDrivingData && dayKm > 0 && (
                          <span className="inline-flex items-center gap-1 bg-white border border-orange-200 text-orange-600 text-xs px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                            <i className="ri-car-line text-xs"></i>
                            {dayKm} km
                          </span>
                        )}
                      </div>

                      {/* Day-start travel banner (major drive to reach first stop) */}
                      {dayData?.dayStart && (
                        <DayStartBanner seg={dayData.dayStart} />
                      )}

                      {/* Activities with between-segment connectors */}
                      <ul className="space-y-0 mb-3">
                        {day.activities.map((act, i) => (
                          <li key={i}>
                            <div className="flex items-start gap-2 text-gray-600 text-sm py-1">
                              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-checkbox-circle-line text-orange-400 text-sm"></i>
                              </div>
                              <span>{act}</span>
                            </div>
                            {/* Between connector: between activities[i] and activities[i+1] */}
                            {dayData?.between && i < day.activities.length - 1 && dayData.between[i] && (
                              <BetweenConnector seg={dayData.between[i]!} />
                            )}
                          </li>
                        ))}
                      </ul>

                      {/* Footer row */}
                      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                        <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                          {day.accommodation !== "——" && (
                            <span className="flex items-center gap-1">
                              <i className="ri-hotel-line"></i>
                              <span>{day.accommodation}</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <i className="ri-restaurant-line"></i>
                            <span>{day.meals}</span>
                          </span>
                        </div>

                        {/* Cumulative mileage */}
                        {hasDrivingData && cumKm > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-orange-400 to-amber-400 h-1.5 rounded-full transition-all duration-500"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              累計 <span className="text-orange-500 font-medium">{cumKm.toLocaleString()}</span> km
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Attractions Tab */}
        {activeTab === "attractions" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {destination.attractions.map((attr) => (
              <div key={attr.name} className="bg-stone-50 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${attr.icon} text-orange-500 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1.5">{attr.name}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{attr.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Cost Tab */}
        {activeTab === "cost" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-lg"></i>
                </div>
                <h3 className="font-bold text-gray-900">報價包含</h3>
              </div>
              <ul className="space-y-3">
                {destination.included.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 bg-green-100 rounded-full">
                      <span className="text-green-600 text-xs font-bold">{i + 1}</span>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 bg-red-100 rounded-full flex items-center justify-center">
                  <i className="ri-close-line text-red-500 text-lg"></i>
                </div>
                <h3 className="font-bold text-gray-900">不包含費用</h3>
              </div>
              <ul className="space-y-3">
                {destination.excluded.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 bg-red-100 rounded-full">
                      <span className="text-red-500 text-xs font-bold">{i + 1}</span>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DestinationTabs;
